import React, { useEffect, useState } from "react";
import { Coins, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/apiClient";

export default function Wallets() {
  const [d, setD] = useState(null);
  const [ledger, setLedger] = useState(null);
  useEffect(() => { api.get("/admin/wallets").then((r) => setD(r.data)).catch(() => toast.error("Failed")); }, []);

  const openLedger = async (cid, name) => {
    setLedger({ loading: true, name });
    try { const { data } = await api.get(`/admin/customers/${cid}`); setLedger({ name, entries: data.wallet_ledger, wallet: data.wallet }); }
    catch { toast.error("Could not load ledger"); setLedger(null); }
  };

  if (!d) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-wallets">
      <div className="flex items-center gap-2"><Coins className="h-5 w-5 text-emerald-400" /><h1 className="font-heading text-2xl font-bold">Customer wallets</h1></div>
      <p className="text-xs text-slate-500">All balances are ledger-derived. Admins can view but never directly edit a wallet.</p>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card label="Total available" value={d.totals.available.display} accent testid="wallets-total-available" />
        <Card label="Total pending" value={d.totals.pending.display} testid="wallets-total-pending" />
        <Card label="Combined" value={d.totals.total.display} testid="wallets-total" />
        <Card label="With balance" value={`${d.totals.customers_with_balance}/${d.totals.total_customers}`} testid="wallets-with-balance" />
      </div>

      <div className="ledger-card overflow-hidden rounded-2xl">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="wallets-table">
            <thead className="bg-slate-900/50 font-mono text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="p-4 text-left">Customer</th><th className="p-4 text-right">Available</th><th className="p-4 text-right">Pending</th><th className="p-4 text-right">Total</th><th className="p-4"></th></tr>
            </thead>
            <tbody>
              {d.wallets.map((w) => (
                <tr key={w.customer_id} className="border-t border-slate-800/60">
                  <td className="p-4"><p className="font-semibold">{w.name || "—"}</p><p className="font-mono text-xs text-slate-500">{w.email}</p></td>
                  <td className="p-4 text-right font-mono text-emerald-400">{w.available_display}</td>
                  <td className="p-4 text-right font-mono text-slate-400">{w.pending_display}</td>
                  <td className="p-4 text-right font-mono">{w.total_display}</td>
                  <td className="p-4 text-right"><button data-testid={`wallet-ledger-${w.customer_id}`} onClick={() => openLedger(w.customer_id, w.name || w.email)} className="rounded-full border border-emerald-500/40 px-3 py-1 text-xs text-emerald-300 hover:bg-emerald-500/10">Ledger</button></td>
                </tr>
              ))}
              {d.wallets.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-500">No wallets yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {ledger && (
        <div className="fixed inset-0 z-[70] flex justify-end" data-testid="wallet-ledger-drawer">
          <div className="absolute inset-0 bg-slate-950/70" onClick={() => setLedger(null)} />
          <div className="relative h-full w-full max-w-lg overflow-y-auto border-l border-slate-800 bg-slate-900 p-6">
            <div className="mb-4 flex items-center justify-between">
              <div><p className="font-heading text-lg font-bold">Wallet ledger</p><p className="text-xs text-slate-500">{ledger.name}</p></div>
              <button onClick={() => setLedger(null)} aria-label="Close" className="text-slate-400"><X className="h-5 w-5" /></button>
            </div>
            {ledger.loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div> : (
              <>
                <div className="mb-4 grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-3"><p className="font-mono text-[11px] uppercase text-slate-500">Available</p><p className="font-heading text-lg font-bold text-emerald-400">{ledger.wallet.available.display}</p></div>
                  <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-3"><p className="font-mono text-[11px] uppercase text-slate-500">Pending</p><p className="font-heading text-lg font-bold">{ledger.wallet.pending.display}</p></div>
                </div>
                <table className="w-full text-sm">
                  <thead className="font-mono text-[11px] uppercase tracking-wider text-slate-500"><tr><th className="p-2 text-left">Date</th><th className="p-2 text-left">Type</th><th className="p-2 text-right">Avail Δ</th></tr></thead>
                  <tbody>
                    {ledger.entries.map((e) => (
                      <tr key={e.entry_id} className="border-t border-slate-800/60">
                        <td className="p-2 text-slate-400">{new Date(e.created_at).toLocaleString("en-IN")}</td>
                        <td className="p-2 font-mono text-xs">{e.entry_type}</td>
                        <td className={`p-2 text-right font-mono ${e.available_delta < 0 ? "text-rose-400" : "text-emerald-400"}`}>₹{(e.available_delta / 100).toLocaleString("en-IN")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ label, value, accent, testid }) {
  return <div className="ledger-card rounded-2xl p-5" data-testid={testid}><p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{label}</p><p className={`mt-2 font-heading text-2xl font-extrabold ${accent ? "text-emerald-400" : "text-slate-100"}`}>{value}</p></div>;
}
